import { Trans } from "@lingui/react/macro";
import {
  Button,
  BaseCard,
  Tag,
  ResponsiveSanityImage,
  SanityImageRatio,
  BlockContentValue
} from "@silversea-ssc/ui-components-react";
import React from "react";
import { BrochureFragment } from "types/graphql-types";
import * as styles from "./BrochureCard.module.scss";
import { BlockContent } from "components/BlockContent";
import { Maybe } from "graphql/jsutils/Maybe";
import clsx from "clsx";
import { useIsTabletOrDesktop } from "src/utils/useIsTabletOrDesktop";

type Props = {
  brochure: BrochureFragment;
  groupTitle?: string | undefined;
  variant?: "vertical" | "horizontal";
  onClick: () => void;
};

const BrochureCard: React.FC<React.PropsWithChildren<Props>> = ({
  brochure,
  groupTitle,
  onClick,
  variant = "vertical"
}) => {
  return (
    <Button
      key="brochure-card"
      renderAs="div"
      onClick={onClick}
      dataContentPiece="view_brochure_card"
      className={styles.buttonWrapper}
    >
      <BaseCard
        background={"white"}
        trackingName={brochure.brochureCode}
        className={clsx(styles.card, {
          [styles.horizontal]: variant === "horizontal"
        })}
        textAlign={"center"}
      >
        <BrochureCardImage
          groupTitle={groupTitle}
          image={brochure.thumbnail}
          alt={brochure.title}
          variant={variant}
        />
        <div
          className={clsx(styles.infoContainer, {
            [styles.horizontal]: variant === "horizontal"
          })}
        >
          <div className={styles.contentContainer}>
            <BrochureContent
              title={brochure.title}
              subtitle={brochure.subtitle}
              description={brochure.description as BlockContentValue}
              variant={variant}
            />
          </div>
          <div className={styles.cta}>
            <Button
              key="brochure-cta"
              variant="primary"
              renderAs="button"
              onClick={onClick}
              dataContentPiece="view_brochure"
              size={"small"}
            >
              <Trans>View Online</Trans>
            </Button>
          </div>
        </div>
      </BaseCard>
    </Button>
  );
};

export default BrochureCard;

type BrochureCardImageProps = {
  groupTitle: string | undefined;
  image: BrochureFragment["thumbnail"];
  alt: string | undefined;
  variant: "vertical" | "horizontal";
};

const BrochureCardImage: React.FC<BrochureCardImageProps> = ({
  groupTitle,
  image,
  alt,
  variant
}) => {
  return (
    <div
      className={clsx(styles.imageContainer, {
        [styles.horizontal]: variant === "horizontal"
      })}
    >
      {groupTitle && (
        <Tag size={"large"} backgroundColor={"neutral15"}>
          {groupTitle.toUpperCase()}
        </Tag>
      )}
      <SanityImageRatio
        image={image}
        className={clsx({ [styles.imageHorizontal]: variant === "horizontal" })}
        ratio={variant === "vertical" ? "card" : "rectangular"}
      >
        <ResponsiveSanityImage
          alt={alt}
          image={image}
          defaultWidth={variant === "vertical" ? 368 : 240}
          defaultHeight={variant === "vertical" ? 279 : 182}
          fit={variant === "vertical" ? "crop" : "original"}
        />
      </SanityImageRatio>
    </div>
  );
};

type BrochureContentProps = {
  title: Maybe<string>;
  subtitle: Maybe<string>;
  description: Maybe<BlockContentValue>;
  variant: "vertical" | "horizontal";
};

const BrochureContent: React.FC<BrochureContentProps> = ({
  title,
  subtitle,
  description,
  variant
}) => {
  const isTabletOrDesktop = useIsTabletOrDesktop();

  return (
    <div
      className={clsx(styles.cardContent, {
        [styles.horizontal]: variant === "horizontal"
      })}
    >
      {title && (
        <div
          className={clsx(styles.title, {
            [styles.horizontal]: variant === "horizontal"
          })}
        >
          {title}
        </div>
      )}
      {subtitle && variant == "vertical" && (
        <div className={styles.subtitle}>{subtitle}</div>
      )}
      {description && (isTabletOrDesktop || variant == "vertical") && (
        <div
          className={clsx(styles.description, {
            [styles.horizontal]: variant === "horizontal"
          })}
        >
          <BlockContent value={description} />
        </div>
      )}
    </div>
  );
};
